/**
 * TimeRuler - Displays time markers above the timeline
 * Click to position playhead
 * Shows frame-level grid when zoomed in (24fps)
 */
import { useMemo, useCallback } from 'react';

// Format time as MM:SS or MM:SS:FF (frames) when showing frames
const formatTime = (ms, showFrames = false) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (showFrames) {
    const frames = Math.floor((ms % 1000) / (1000 / 24));
    return `${minutes}:${String(seconds).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
  }
  
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export function TimeRuler({
  totalDuration,
  pxPerMs,
  totalWidth,
  labelWidth,
  playheadPosition,
  scrollLeft,
  onSeek
}) {
  // Calculate tick intervals based on zoom level
  const { majorTicks, minorTicks, showFrames } = useMemo(() => {
    const majors = [];
    const minors = [];
    
    // Calculate pixels per frame (24fps = 1 frame every ~41.67ms)
    const msPerFrame = 1000 / 24;
    const pxPerFrame = pxPerMs * msPerFrame;
    
    // Show frame marks if zoomed in enough (each frame > 3px apart)
    const shouldShowFrames = pxPerFrame > 3;
    
    // Calculate visible range for optimization
    const visibleStart = Math.max(0, scrollLeft / pxPerMs - 1000);
    const visibleEnd = Math.min(totalDuration, (scrollLeft + 2000) / pxPerMs + 1000);
    
    if (shouldShowFrames) {
      // Frame-level view: Major = seconds, Minor = frames
      const majorInterval = 1000; // Every second
      
      // Generate major ticks (seconds)
      const startMajor = Math.floor(visibleStart / majorInterval) * majorInterval;
      for (let t = startMajor; t <= visibleEnd; t += majorInterval) {
        if (t >= 0 && t <= totalDuration) {
          majors.push({ time: t, x: t * pxPerMs });
        }
      }
      
      // Generate minor ticks (frames) - 24 per second
      const startFrame = Math.floor(visibleStart / msPerFrame) * msPerFrame;
      for (let t = startFrame; t <= visibleEnd; t += msPerFrame) {
        if (t >= 0 && t <= totalDuration) {
          // Skip if it's on a second boundary (major tick)
          if (Math.abs(t % 1000) > 5) {
            minors.push({ time: t, x: t * pxPerMs });
          }
        }
      }
    } else {
      // Normal view - determine interval based on zoom
      let majorInterval;
      let minorInterval;
      
      if (pxPerMs > 0.02) {
        // Zoomed in - show every 5 seconds
        majorInterval = 5000;
        minorInterval = 1000;
      } else if (pxPerMs > 0.005) {
        // Normal - show every 10 seconds
        majorInterval = 10000;
        minorInterval = 5000;
      } else if (pxPerMs > 0.002) {
        // Zoomed out - show every 30 seconds
        majorInterval = 30000;
        minorInterval = 10000;
      } else {
        // Very zoomed out - show every minute
        majorInterval = 60000;
        minorInterval = 30000;
      }
      
      // Generate major ticks
      const startMajor = Math.floor(visibleStart / majorInterval) * majorInterval;
      for (let t = startMajor; t <= visibleEnd; t += majorInterval) {
        if (t >= 0 && t <= totalDuration) {
          majors.push({ time: t, x: t * pxPerMs });
        }
      }
      
      // Generate minor ticks
      const startMinor = Math.floor(visibleStart / minorInterval) * minorInterval;
      for (let t = startMinor; t <= visibleEnd; t += minorInterval) {
        if (t >= 0 && t <= totalDuration && t % majorInterval !== 0) {
          minors.push({ time: t, x: t * pxPerMs });
        }
      }
    }
    
    return { 
      majorTicks: majors, 
      minorTicks: minors, 
      showFrames: shouldShowFrames 
    };
  }, [totalDuration, pxPerMs, scrollLeft]);

  // Handle click on ruler to seek
  const handleClick = useCallback((e) => {
    if (!onSeek) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollLeft;
    const timeMs = x / pxPerMs;
    const clampedTime = Math.max(0, Math.min(timeMs, totalDuration));
    onSeek(clampedTime);
  }, [onSeek, scrollLeft, pxPerMs, totalDuration]);

  return (
    <div className="h-8 bg-zinc-900 border-b border-zinc-800 flex shrink-0">
      {/* Track label area - sticky on left */}
      <div 
        className="shrink-0 border-r border-zinc-800 flex items-center justify-center sticky left-0 z-10 bg-zinc-900"
        style={{ width: labelWidth }}
      >
        <span className="text-[10px] text-zinc-600">Time</span>
      </div>
      
      {/* Time markers - clickable */}
      <div 
        className="relative flex-1 overflow-hidden cursor-pointer"
        onClick={handleClick}
      >
        <div 
          className="absolute top-0 h-full"
          style={{ width: totalWidth, transform: `translateX(-${scrollLeft}px)` }}
        >
          {/* Minor ticks (frames when zoomed in, otherwise sub-intervals) */}
          {minorTicks.map((tick, i) => (
            <div
              key={`minor-${i}`}
              className="absolute bottom-0 w-px bg-zinc-700 pointer-events-none"
              style={{ 
                left: tick.x,
                height: showFrames ? '4px' : '8px'
              }}
            />
          ))}
          
          {/* Major ticks with labels */}
          {majorTicks.map((tick, i) => (
            <div
              key={`major-${i}`}
              className="absolute top-0 h-full pointer-events-none"
              style={{ left: tick.x }}
            >
              <div className="h-full w-px bg-zinc-600" />
              <span 
                className="absolute top-1 left-1 text-[10px] text-zinc-400 whitespace-nowrap"
              >
                {formatTime(tick.time, showFrames)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
